import { useState } from "react";
import DataTable from "react-data-table-component";
import CustomLoader from "../../Components/custom/CustomLoader";
import PaginationControl from "../../Components/custom/PaginationControl";
import SearchBar from "../../Components/custom/SearchBar";
import moment from "moment";
import Filter from "../../Components/custom/Filter";
import { useFetch, useMutate } from "../../hooks/queries";
import { useNavigate } from "react-router-dom";

const ListCourses = (props) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const navigate = useNavigate();

  const [startDate] = useState(moment().subtract(7, "d").format("YYYY-MM-DD"));
  const [endDate] = useState(moment().format("YYYY-MM-DD"));

  const [selectedSchool, setSelectedSchool] = useState({
    label: "All Schools",
    value: 0,
  });

  const [selectedOperator, setSelectedOperator] = useState({
    label: "N/A",
    value: 0,
  });

  const handleFilterApply = ({ operator, school }) => {
    setSelectedOperator(
      operator || {
        label: "N/A",
        value: 0,
      },
    );
    setSelectedSchool(
      school || {
        label: "All Schools",
        value: 0,
      },
    );
    setPage(1);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setPage(1);
  };

  const handleSort = (column, sortDirection) => {
    const columnFieldMap = {
      courseId: "CourseId",
      courseName: "CourseName",
      operatorName: "OperatorName",
      schoolName: "SchoolName",
      term: "Term",
      students: "Students",
      status: "Status",
    };
    setSortBy(columnFieldMap[column.id]);
    setSortOrder(sortDirection);
    setPage(1);
  };

  const columns = [
    {
      id: "courseId",
      name: "Course Id",
      selector: (row) => row.courseId,
      sortable: true,
      width: "150px",
    },
    {
      id: "courseName",
      name: "Course Name",
      selector: (row) => row.courseName,
      sortable: true,
    },
    {
      id: "operatorName",
      name: "Operator",
      selector: (row) => (row.operatorName == null ? "N/A" : row.operatorName),
      sortable: true,
      width: "150px",
    },
    {
      id: "schoolName",
      name: "School",
      selector: (row) => (row.schoolName == null ? "N/A" : row.schoolName),
      sortable: true,
      width: "180px",
    },
    {
      id: "term",
      name: "Term",
      selector: (row) => row.term,
      sortable: true,
    },
    {
      id: "students",
      name: "Total Students",
      selector: (row) => row.students,
      sortable: true,
    },
    {
      id: "status",
      name: "Status",
      selector: (row) =>
        row.status === "available"
          ? "Published"
          : row.status === "completed"
            ? "Completed"
            : "Unpublished",
      sortable: true,
    },
  ];

  const buildCoursesRequestBody = () => {
    const ot = localStorage.getItem("OTsReD");
    const st = localStorage.getItem("STsReD");

    const safeParseInt = (value) => {
      const parsed = parseInt(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    const parsedOt = safeParseInt(ot);
    const parsedSt = safeParseInt(st);
    const selectedOp = selectedOperator?.value;
    const selectedSch = selectedSchool?.value;

    const data = {
      OperatorId: 0,
      SchoolId: 0,
      StartDate: startDate,
      EndDate: endDate,
      PageNumber: page,
      PageSize: rowsPerPage,
      studentsLastLoginFilter: [],
      SearchText: searchText,
      SortBy: sortBy,
      SortOrder: sortOrder,
    };

    if (parsedOt !== 0 && parsedSt === 0) {
      data.OperatorId = parsedOt;
      data.SchoolId = safeParseInt(selectedSch);
    } else if (parsedOt !== 0 && parsedSt !== 0) {
      data.OperatorId = parsedOt;
      data.SchoolId = parsedSt;
    } else {
      data.OperatorId = safeParseInt(selectedOp);
      data.SchoolId = safeParseInt(selectedSch);
    }

    return data;
  };

  const {
    data: coursesResponse,
    isPending,
    isLoading,
    isFetching,
  } = useFetch({
    queryKey: [
      "courses",
      page,
      rowsPerPage,
      startDate,
      endDate,
      selectedSchool.value,
      selectedOperator.value,
      searchText,
      sortBy,
      sortOrder,
      localStorage.getItem("OTsReD"),
      localStorage.getItem("STsReD"),
    ],
    url: `${process.env.REACT_APP_API_URL}Courses/GetCourses/`,
    method: "PATCH",
    data: buildCoursesRequestBody(),
  });

  const isCoursesLoading = isPending || isLoading || isFetching;
  const courses = coursesResponse?.data || [];
  const totalRows = coursesResponse?.totalCount || 0;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(1);
  };

  const buildExportPayload = (filterData) => {
    const school = filterData?.school || selectedSchool;
    const operator = filterData?.operator || selectedOperator;

    return {
      startDate: startDate,
      endDate: endDate,
      schoolId: school.value == null ? 0 : school.value,
      operatorId: operator.value == null ? 0 : operator.value,
    };
  };

  const downloadCoursesFile = (data) => {
    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveBlob(blob, "Courses.xlsx");
    } else {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", "Courses.xlsx");
      document.body.appendChild(link);
      link.download = "Courses.xlsx";
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportCoursesMutation = useMutate({
    mutationKey: ["exportCourses"],
    url: `${process.env.REACT_APP_API_URL}CanvasUsers/ExportCourses`,
    method: "PATCH",
    axiosConfig: {
      responseType: "arraybuffer",
    },
    onSuccess: (data) => {
      downloadCoursesFile(data);
    },
  });

  const exportingCourses =
    exportCoursesMutation.isPending || exportCoursesMutation.isLoading;

  return (
    <div className="content">
      <Filter
        isLoading={isCoursesLoading}
        onApply={handleFilterApply}
        applyButtonClassName="btn btn-success btn-sm w-100"
        showExport={true}
        exporting={exportingCourses}
        exportLabel="Export CSV"
        onExport={(filterData) =>
          exportCoursesMutation.mutate(buildExportPayload(filterData))
        }
      />

      <div className="left-side">
        <div className="container-fluid mt-30">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Courses List</h3>
              {/* <div style={{ width: "300px" }}>
                <SearchBar
                  value={searchText}
                  onSearch={handleSearch}
                  onClear={handleClearSearch}
                  placeholder="Search courses..."
                  debounceMs={500}
                  disabled={isCoursesLoading}
                />
              </div> */}
            </div>
            <div className="card-body p-0">
              <DataTable
                data={courses}
                columns={columns}
                progressPending={isCoursesLoading}
                progressComponent={<CustomLoader />}
                fixedHeader
                fixedHeaderScrollHeight={"65vh"}
                defaultSortFieldId={1}
                onRowClicked={(e) =>
                  navigate(`/Courses/assignments/${e.courseId}`, {
                    state: {
                      courseName: e.courseName,
                      operatorName: e.operatorName,
                    },
                  })
                }
                pointerOnHover
                onSort={handleSort}
                sortServer
              />
              <PaginationControl
                totalCount={totalRows}
                currentPage={page}
                pageSize={rowsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePerRowsChange}
                pageSizeOptions={[10, 20, 50, 100, 200]}
                disabled={isCoursesLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListCourses;
